import { memo, useCallback, useEffect } from 'react';
import NextImage from 'next/image';
import Head from 'next/head';

import {
  VerifyEmailBoxWrapper,
  VerifyEmailWrapper,
  VerifyEmailError,
} from './styles';
import Title from '../common/title';
import Text from '../common/text';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { useResendEmail } from 'lib/mutation/resend-email';
import { Loader } from '../common/loader';
import { Container } from '../common/container';
import { useScrollBlock } from 'hooks/useScrollBlock';
import { toggleIsVerifiedBox } from 'store/slices/global';
import { authSelector } from 'store/slices/auth/selector';

function VerifyEmail() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(authSelector);
  const { resend, isError, error, data, isLoading } = useResendEmail();
  const { allowScroll, blockScroll } = useScrollBlock();

  const closeIsVerifyBox = useCallback(() => {
    dispatch(toggleIsVerifiedBox());
  }, [dispatch]);

  useEffect(() => {
    blockScroll();
    return () => {
      allowScroll();
    };
  }, [allowScroll, blockScroll]);

  function onResendEmailClick() {
    // Disabling rules because this part will be deprecated
    /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain */
    resend({ email: user.data?.email! });
    /* eslint-enable */
  }

  return (
    <VerifyEmailWrapper>
      <Head>
        <title>Verify Account</title>
      </Head>
      <header className="verify-header">
        <Container size="lg">
          <div className="verify-header-logo" onClick={closeIsVerifyBox}>
            <NextImage src="/static/logo-1.png" width={25} height={40} />
          </div>
          <div className="verify-header-logo-mobile" onClick={closeIsVerifyBox}>
            <NextImage src="/static/logo-2.svg" width={105} height={25} />
          </div>
        </Container>
      </header>
      <VerifyEmailBoxWrapper>
        <div className="verify">
          <NextImage
            src="/static/icons/verify-email.svg"
            width={100}
            height={100}
          />

          <Title type="mode3" className="verify-title">
            <span>Verify your e-mail</span>
            {` to finish signing up to Richr.`}
          </Title>

          <div className="verify-desc">
            <Text type="mode8" className="verify-desc-1">
              <span>{user.data?.fullName}</span>
              {` please confirm that `}
              <span>{user.data?.email}</span>
              {` is your email address by going to your inbox and verifying your email
          address.`}
            </Text>

            <Text type="mode8" className="verify-desc-2">
              {`Sometimes emails go to spam or your junk mail folder, please
          check there if you dont see an email from us.`}
            </Text>

            {!data && !isLoading && (
              <button
                className="verify-desc-resend"
                onClick={onResendEmailClick}
                type="button"
              >
                <Text type="mode8">Resend email</Text>
              </button>
            )}

            {isLoading && <Loader />}

            {isError && (
              <VerifyEmailError>
                Next Available Time: {error?.response?.data.nextAvailableTime}
              </VerifyEmailError>
            )}

            {data && data.success && (
              <NextImage
                src="/static/icons/checkmarks.svg"
                width={24}
                height={24}
              />
            )}
          </div>
          <Text className="verify-bottom" type="mode8">
            {`Need help? Ask at `}
            <a href="mailto:hello@richr.com">hello@richr.com</a>
            {` or call `}
            <a href="tel:+18449574247">+1-844-957-4247</a>
          </Text>
        </div>
      </VerifyEmailBoxWrapper>
    </VerifyEmailWrapper>
  );
}

export default memo(VerifyEmail);
